import React from 'react'
import { Link, generatePath } from 'react-router-dom'
import { ISnackbarObject, IUserObject } from 'types'
import Button from 'components/button'
import { Copy, Dots } from 'components/icons'
import { ORGANISATION_TEAMS } from 'routes'
import { formatShortDate } from 'helpers/date'

export const secondsToHms = (d: number) => {
  d = Number(d)
  const h = Math.floor(d / 3600)
  const m = Math.floor((d % 3600) / 60)
  const s = Math.floor((d % 3600) % 60)

  const hDisplay = h > 0 ? h + (h == 1 ? ' hour, ' : ' hours, ') : ''
  const mDisplay = m > 0 ? m + (m == 1 ? ' minute, ' : ' minutes, ') : ''
  const sDisplay = s > 0 ? s + (s == 1 ? ' second' : ' seconds') : ''
  return hDisplay + mDisplay + sDisplay
}

export const mapUsersToTableData = (
  users: IUserObject[],
  onLogin: (user: IUserObject) => void,
  verifyEmail: (user: IUserObject) => void,
  handleEditUser: (user: IUserObject) => void,
  remove2fa: (user: IUserObject) => void,
  setSnackbarMessage: (newSnackbarObject: ISnackbarObject) => void,
  onShowOptions?: (user: IUserObject, event: React.MouseEvent<HTMLElement>) => void
) => {
  return users.map((user: IUserObject) => {
    return {
      email: {
        label: (
          <div className="flex items-center">
            {user.email}
            <div
              className="cursor-pointer hover:bg-surface-sunken transition-all rounded p-[5px] max-w-fit ml-auto"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(user.email)
                  setSnackbarMessage({
                    status: 'success',
                    message: 'Email copied to clipboard'
                  })
                } catch {
                  setSnackbarMessage({
                    status: 'error',
                    message: 'Failed to copy email'
                  })
                }
              }}
            >
              <Copy />
            </div>
          </div>
        ),
        value: user.email
      },
      isDeleted: {
        label: user.isDeleted ? 'Yes' : 'No',
        value: user.isDeleted ? 'Yes' : 'No'
      },
      publicId: {
        label: (
          <div className="flex items-center">
            {user.publicId.slice(0, 5)}...
            <div
              className="cursor-pointer hover:bg-surface-sunken transition-all rounded p-[5px] max-w-fit ml-auto"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(user.publicId)
                  setSnackbarMessage({
                    status: 'success',
                    message: 'Public ID copied to clipboard'
                  })
                } catch {
                  setSnackbarMessage({
                    status: 'error',
                    message: 'Failed to copy public ID'
                  })
                }
              }}
            >
              <Copy />
            </div>
          </div>
        ),
        value: user.publicId
      },
      createdAt: {
        label: user.createdAt ? formatShortDate(user.createdAt) : '',
        value: user.createdAt ? user.createdAt : ''
      },
      paid: {
        label: (
          <span
            className={`text-sm ${
              user.subscriptionLevel === 1 && !user.isOnFreeTrial
                ? 'bg-light-green'
                : user.subscriptionLevel === 1 && user.isOnFreeTrial
                  ? 'bg-light-orange'
                  : 'bg-light-blue'
            } rounded border border-divider p-[5px]`}
          >
            {user.subscriptionLevel === 1 ? (user.isOnFreeTrial ? 'Trial' : 'Yes') : 'No'}
          </span>
        ),
        value: user.subscriptionLevel === 1 ? 'Yes' : 'No'
      },
      team: {
        label: user.team ? (
          <Link
            to={generatePath(ORGANISATION_TEAMS, { organisationId: user.team.orgPublicId })}
            className="text-blue hover:underline"
          >
            {user.team.orgName} › {user.team.teamName}
          </Link>
        ) : (
          <span className="text-muted italic">No Org</span>
        ),
        value: user.team ? `${user.team.orgName} › ${user.team.teamName}` : 'No Org'
      },
      managedSeatCount: {
        label: user.managedSeatCount != null ? user.managedSeatCount : '-',
        value: user.managedSeatCount != null ? user.managedSeatCount : 0
      },
      invitedBy: { label: user.invitedBy, value: user.invitedBy ? user.invitedBy : '' },
      invitesSent: { label: user.invitesSent, value: user.invitesSent ? user.invitesSent : 0 },
      projectsOn: { label: user.projectsOn, value: user.projectsOn ? user.projectsOn : 0 },
      allowSupportAccess: {
        label: <span data-testid={`support-access-${user.publicId}`}>{user.allowSupportAccess ? 'Yes' : 'No'}</span>,
        value: user.allowSupportAccess ? 'Yes' : 'No'
      },
      verified: {
        label: user.isEmailVerified ? (
          'Yes'
        ) : (
          <Button internalType="danger" onClick={() => verifyEmail(user)}>
            Verify
          </Button>
        ),
        value: user.isEmailVerified ? 'Yes' : 'No'
      },
      isSuperAdmin: { label: user.isSuperAdmin ? 'Yes' : 'No', value: user.isSuperAdmin ? 'Yes' : 'No' },
      isRateLimited: {
        label: user.isRateLimited ? (
          <span className="text-sm bg-light-red rounded border border-divider p-[5px]">Yes</span>
        ) : (
          'No'
        ),
        value: user.isRateLimited ? 'Yes' : 'No'
      },
      is2faEnabled: {
        label: user.is2FaEnabled ? (
          <Button internalType="danger" onClick={() => remove2fa(user)}>
            Remove
          </Button>
        ) : (
          'No'
        ),
        value: user.is2FaEnabled ? 'Yes' : 'No'
      },
      options: {
        label: (
          <Button
            internalType="transparent"
            aria-label={`User options${user.email ? ` for ${user.email}` : ''}`}
            onClick={(event) => onShowOptions?.(user, event)}
          >
            <Dots />
          </Button>
        ),
        value: ''
      }
    }
  })
}
